import { useEffect, useMemo, useState } from "react";
import {getSpotsAllPages, getSpotTags, getSpotTagsBySpot} from "../../api/spot/spot.api";
import {getRegionTree} from "../../api/region/region.api";
import QREditForm from "../form/QREditForm";
import EntryPointPreviewPopup from "../form/entrypoint/EntryPointPreviewPopup";
import {Table, Pagination, Button, Alert, Tag} from "omoplay";
import moment from "moment";
import MiniMenu from "../form/MiniMenu";
import GenericBoard from "../form/GenericBoard";
import SearchInput from "../SearchInput";
import {matchesSpotKeyword, spotShortId, spotTypeLabel} from "../../libs/spot";
import SpotTagManagePopup from "../form/entrypoint/SpotTagManagePopup";

const PAGE_SIZE = 10

const QRList = () => {
  const [spotData, setSpotData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(1);
  const [spots, setSpots] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [spotId, setSpotId] = useState();
  const [tableData, setTableData] = useState([])
  const [openMenuId, setOpenMenuId] = useState(null)
  // 지역(주소) 트리는 여기서 1회만 조회해 안정적으로 보관 → 팝업 remount에도 항상 준비된 상태로 전달
  const [regionData, setRegionData] = useState([])
  // 진입점 그룹(해시태그) — 2026-08-27
  const [tags, setTags] = useState([])
  const [tagsBySpot, setTagsBySpot] = useState({})
  const [activeTag, setActiveTag] = useState(null)   // 목록 필터로 선택된 그룹
  const [showTagManage, setShowTagManage] = useState(false)

  useEffect(() => {
    getRegionTree().then(({data}) => setRegionData(data ?? []))
  }, [])

  const headers = [
    { name: "고객 진입점 (QR)" },
    { name: "타입", width: "120px" },
    { name: "ID", width: "130px" },
    { name: "생성일", sortable: true, width: "166px" },
    { name: "", align: "center", width: "80px" },
  ]

  // 전체를 1회만 조회한다. 진입점 추가/수정은 QREditForm 이 저장 후 window.location.reload() 하므로
  // 이 화면에서 별도 재조회를 하지 않아도 목록이 최신이다.
  useEffect(() => {
    getSpotsAllPages().then(({data}) => setSpots(data.qrSpots ?? []))
  }, [])

  // 그룹 정보는 목록·관리 팝업이 함께 쓴다. 변경 후에는 loadTags() 로 다시 받는다.
  const loadTags = () => Promise.all([getSpotTags(), getSpotTagsBySpot()])
      .then(([tagRes, bySpotRes]) => {
        setTags(tagRes.data ?? [])
        setTagsBySpot(bySpotRes.data ?? {})
      })

  useEffect(() => {
    loadTags()
  }, [])

  const filteredSpots = useMemo(
      () => spots.filter((spot) =>
          matchesSpotKeyword(spot, keyword)
          // 그룹 칩을 누르면 그 그룹 소속만 본다. 검색어와 AND 로 걸린다.
          && (activeTag == null || (tagsBySpot[spot.id] ?? []).includes(activeTag))
      ),
      [spots, keyword, activeTag, tagsBySpot]
  )

  const filteredCount = filteredSpots.length

  const pagedSpots = useMemo(
      () => filteredSpots.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
      [filteredSpots, page]
  )

  // 검색·삭제로 결과가 줄어들어 현재 페이지가 범위를 넘으면 빈 화면이 된다 → 마지막 페이지로 당긴다.
  // 의존성은 filteredCount 로 둔다(배열에 `filteredSpots.length` 같은 식을 직접 쓰면
  // react-hooks/exhaustive-deps 경고가 나고, CRA 는 CI=true 에서 경고를 빌드 에러로 올린다).
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(filteredCount / PAGE_SIZE))
    if (page > lastPage) {
      setPage(lastPage)
    }
  }, [filteredCount, page])

  useEffect(() => {
    setTableData(pagedSpots.map((spot) => {
      return [
        <div className="flex-align-center h-full"><Tag color="info">{spotTypeLabel(spot.type)}</Tag></div>,
          <div className="flex-col h-full justify-center">
              <div className="text-primary pointer" onClick={() => setSpotId(spot.id)}>
                  {spot.name}
              </div>
              {
                  /* 소속 그룹을 행에서 바로 보여준다 — 어느 매장이 어느 그룹인지 목록에서 확인 가능해야 한다 */
                  (tagsBySpot[spot.id] ?? []).length > 0 && (
                      <div className="text-xs text-gray-500 truncate">
                          {(tagsBySpot[spot.id] ?? []).map((c) => `#${c}`).join(" ")}
                      </div>
                  )
              }
          </div>,
        <div className="flex-align-center h-full"><Tag>{spotShortId(spot.uid)}</Tag></div>,
        <div className="flex-align-center h-full">{moment(spot.createdAt).format("YYYY.MM.DD HH:mm")}</div>,
        <div className="flex-center-all h-full">
          <MiniMenu
              id={spot.id}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              onClickEdit={() => {
                setOpenMenuId(null) // 편집 진입 시 미니메뉴 닫기(팝업 위에 남는 문제 방지)
                setSpotData(spots.find((_spot) => _spot.id === spot.id))
                setShowForm(true)
              }}
          />
        </div>
      ]
    }))
  }, [openMenuId, spots, pagedSpots, tagsBySpot]);

  return (
    <div className="flex-col gap-2">
      <div className="flex gap-4 items-center justify-between">
        <div style={{flexGrow: "1"}}>
          <Alert type="info" title="고객 진입점은 고객들이 프로젝트(이벤트)에 참여하기 위해 접하게 되는 접점으로 ‘URL링크'와 ‘QR’이 제공됩니다." withIcon={true} />
        </div>
        <div>
          <Button variants="primary"
                  onClick={() => {
                    setShowForm(true)
                    setSpotData(null)
                  }}
                  value="+ 진입점 추가"
          />
        </div>
      </div>
      {/* 진입점 그룹(해시태그) — 목록 상단. 칩을 누르면 그 그룹만 필터된다. */}
      <div className="flex gap-4 items-center justify-between mt-4">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="text-sm font-semibold whitespace-nowrap">그룹</div>
          <div className="pointer" onClick={() => setActiveTag(null)}>
            <Tag color={activeTag == null ? "info" : undefined}>{`전체 (${spots.length})`}</Tag>
          </div>
          {
            tags.map((t) => (
              <div key={t.tagCode} className="pointer"
                   onClick={() => setActiveTag(activeTag === t.tagCode ? null : t.tagCode)}>
                <Tag color={activeTag === t.tagCode ? "info" : undefined}>
                  {`# ${t.tagCode} (${t.spotCount})`}
                </Tag>
              </div>
            ))
          }
          {tags.length === 0 && (
            <div className="text-sm text-gray-400">그룹을 만들면 진입점을 묶어서 한 번에 고를 수 있습니다.</div>
          )}
        </div>
        <div>
          <Button variants="default" value="그룹 관리" onClick={() => setShowTagManage(true)} />
        </div>
      </div>
      {/* items-center 로 카운트 텍스트를 입력창(40px) 세로 중앙에 맞춘다.
          items-end 는 두 요소의 '아래끝'을 맞춰서 카운트가 입력창 중심보다 10px 내려가 보였다.
          mt-4 = 위 Alert 와의 간격 확보(부모 flex-col gap-2 의 8px 만으로는 붙어 보인다). */}
      <div className="flex gap-4 items-center justify-between mt-4">
        <div className="max-w-[405px] w-[405px]">
          <SearchInput
              instant={true}
              placeholder="진입점명, ID, 타입으로 검색"
              onSubmit={(text) => {
                setPage(1) // 검색어가 바뀌면 항상 1페이지부터 본다
                setKeyword(text)
              }}
          />
        </div>
        {/* whitespace-nowrap = '검색 결과 613개 / 전체 613개' 가 좁은 폭에서 줄바꿈되며
            행 높이를 밀어 정렬이 깨지는 것을 막는다. */}
        <div className="text-sm text-gray-500 whitespace-nowrap">
          {/* 필터 조합 4가지가 모두 명확히 읽혀야 한다:
                없음 / 검색만 / 그룹만 / 둘 다 */}
          {(() => {
              const parts = []
              if (activeTag != null) parts.push(`#${activeTag}`)
              if (keyword.trim() !== "") parts.push("검색 결과")
              return parts.length === 0
                  ? `전체 ${spots.length}개`
                  : `${parts.join(" · ")} ${filteredCount}개 / 전체 ${spots.length}개`
          })()}
        </div>
      </div>
      <div className="flex-col gap-2">
          <GenericBoard items={tableData} columns={[
              { align: 'left', width: 'w-20' },
              { align: 'left', flex: true },
              { align: 'left' },
              { align: 'right', width: 'w-32' },
              { align: 'right' }
          ]} />
        <div className="flex justify-center">
          <Pagination pageSize={PAGE_SIZE} totalCount={filteredCount} currentPage={page} onPaging={(page) => setPage(page)} />
        </div>
      </div>
      <QREditForm
          key={spotData?.id ?? 'new'} /* 스팟이 바뀌면 remount → 내부 상태(TreeChoiceForm 등) 재초기화 */
          visible={showForm}
          qrSpot={spotData}
          regionData={regionData}
          onClose={() => setShowForm(false)}
      />
      <SpotTagManagePopup
          visible={showTagManage}
          spots={spots}
          tags={tags}
          tagsBySpot={tagsBySpot}
          onChanged={() => loadTags()}
          onClose={() => setShowTagManage(false)}
      />
        <EntryPointPreviewPopup show={spotId != null} spotId={spotId} onClose={() => setSpotId(null)} />
    </div>
  );
};
export default QRList;
